import { useCallback, useEffect, useRef, useState } from "react"
import { useAppSession } from "~/ui-rtk/pages/Auth/useCubeApiToken"

export interface ChartDataExportOptions {
  filename?: string
  dateFormat?: string
  delimiter?: string
  compareData?: ChartDataPoint[]
}

export interface ChartDataPoint {
  [key: string]: unknown
  date?: string
  dateKey?: string
}

export interface ExportProgress {
  progress: number
  message?: string
}

export interface ExportResult {
  filename: string
  downloadUrl: string
  rowCount: number
  columnCount: number
}

export function useChartDataExportWorker() {
  const [isExporting, setIsExporting] = useState(false)
  const [progress, setProgress] = useState<ExportProgress>({ progress: 0 })
  const [error, setError] = useState<string | null>(null)
  const workerRef = useRef<Worker | null>(null)
  const { session } = useAppSession()

  const handleExportComplete = useCallback((result: ExportResult) => {
    // Create a temporary link to download the file
    const link = document.createElement("a")
    link.href = result.downloadUrl
    link.download = result.filename
    link.style.display = "none"

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the blob URL
    setTimeout(() => {
      URL.revokeObjectURL(result.downloadUrl)
    }, 100)
  }, [])

  useEffect(() => {
    // Initialize worker
    workerRef.current = new Worker("/workers/csv-export-worker.js")

    workerRef.current.onmessage = event => {
      const { type, payload } = event.data

      switch (type) {
        case "CUBE_API_READY":
          break
        case "PROGRESS":
          setProgress(payload)
          break
        case "EXPORT_COMPLETE":
          setIsExporting(false)
          setProgress({ progress: 100, message: "Export complete!" })
          handleExportComplete(payload)
          break
        case "ERROR":
          setIsExporting(false)
          setError(payload.message || "Export failed")
          setProgress({ progress: 0 })
          break
      }
    }

    workerRef.current.onerror = error => {
      setIsExporting(false)
      setError(`Worker error: ${error.message}`)
      setProgress({ progress: 0 })
    }

    // Initialize cube API in worker
    if (session?.cube && workerRef.current) {
      workerRef.current.postMessage({
        type: "INIT_CUBE_API",
        payload: {
          apiUrl: `${import.meta.env.VITE_CUBE_API_URL}/cubejs-api/v1`,
          token: session.cube.token,
        },
      })
    }

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate()
        workerRef.current = null
      }
    }
  }, [session, handleExportComplete])

  const exportChartDataToCSV = useCallback(
    (
      data: ChartDataPoint[],
      metrics: string[],
      options: ChartDataExportOptions = {},
    ) => {
      if (!workerRef.current) {
        setError("Worker not initialized")
        return
      }

      if (!data || data.length === 0) {
        setError("No chart data available for export")
        return
      }

      if (!metrics || metrics.length === 0) {
        setError("No metrics specified for export")
        return
      }

      setIsExporting(true)
      setError(null)
      setProgress({ progress: 0, message: "Starting export..." })

      workerRef.current.postMessage({
        type: "EXPORT_CHART_DATA_CSV",
        payload: {
          data,
          metrics,
          options: {
            filename: options.filename,
            dateFormat: options.dateFormat,
            delimiter: options.delimiter,
            compareData: options.compareData,
          },
        },
      })
    },
    [],
  )

  const resetExport = useCallback(() => {
    setIsExporting(false)
    setError(null)
    setProgress({ progress: 0 })
  }, [])

  return {
    exportChartDataToCSV,
    isExporting,
    progress,
    error,
    resetExport,
  }
}
